import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Image from 'next/future/image';
import { VariantProps } from './types';
import { generateColors, generateSelection } from './helpers';
import PremiumCondition from './PremiumCondition';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import { ConditionPopup } from '@/components/products/conditionPopup/ConditionPopup';
import BatteryHealthModal from '../../BatteryHealthModal';

const BatteryHealthOption = dynamic(() => import('./BatteryHealthOption'));
const DefaultOption = dynamic(() => import('./DefaultOption'));
const ConditionOption = dynamic(() => import('./ConditionOption'));
const ColorOption = dynamic(() => import('./ColorOption'));

const OPTION_STYLE =
  'text-xxs sm:text-xs mt-1 w-1/2 xxs:w-1/3 min-w-fit pr-2 xs:w-1/4';

const FULL_WIDTH_OPTION_STYLE =
  'text-xxs sm:text-xs mt-1 w-1/2 min-w-fit pr-2 xxs:w-1/4';

const colorHandles = ['color', 'watchCaseColor', 'watchBandColor'];

const streamlineOption = (handle: string, value: string) => {
  if (handle === 'dexterity') return `${value} Handed`;

  if (handle === 'flex') return `${value} Flex`;

  if (handle === 'length') return `${value} Length`;
  if (handle === 'loft') return `${value}° Loft`;

  if (handle === 'lieAngle') return `${value} Lie Angle`;

  return value;
};

const generateVariantNameLabel = (name: string, handle: string) => {
  if (handle === 'batteryHealth') return 'Battery Health Guaranteed';

  return name;
};

const Variant: React.FC<VariantProps> = ({
  rVariant,
  offer,
  offerTree,
  selection,
  product,
  wasSuggested,
  setWasSuggested,
  categoryParentName,
}) => {
  const { handle, name, values } = rVariant;
  const router = useRouter();

  // State to track if a variant was suggested
  const [isBatteryHealthVisible, setIsBatteryHealthVisible] = useState(false);

  const [isConditionVisible, setIsConditionVisible] = useState(false);
  /**
   * Determines the offer's initial color based on variant handle.
   */
  const generateOfferColor = () =>
    colorHandles.includes(handle)
      ? offer?.rVariants?.find((v) => v.handle === handle)?.value ?? ''
      : '';

  const offerColor = generateOfferColor();
  const [currentColor, setCurrentColor] = useState(offerColor);

  useEffect(() => {
    setCurrentColor(offerColor);
  }, [offerColor]);

  /**
   * Checks if the current variant is a color variant.
   */
  const isColorVariant = useMemo(() => colorHandles.includes(handle), [handle]);

  const categoryPk = product.categoryPks?.[0] ?? offer.categoryPks?.[0] ?? '';
  const category = product.category ?? offer.categories?.[0] ?? '';

  // Hide color variant for smartwatches
  if (category === 'Smartwatches' && handle === 'color') return null;
  // Hide Screen Size for Laptops
  if (category === 'Laptops' && handle === 'screenSize') return null;

  /**
   * Generates color mapping for different product types.
   */
  const getColors = () => {
    if (handle === 'watchBandColor') {
      return generateColors(
        product.watchBandColors,
        product.watchBandColorsInHex,
      );
    }

    if (handle === 'watchCaseColor') {
      return generateColors(
        product.watchCaseColors,
        product.watchCaseColorsInHex,
      );
    }

    return generateColors(product.colors, product.colorsInHex);
  };

  /**
   * Handles modal visibility for battery health and condition.
   */
  const handleVisibleModal = () => {
    if (handle === 'batteryHealth') setIsBatteryHealthVisible(true);
    if (handle === 'condition') setIsConditionVisible(true);
  };

  const needsMoreInfo = ['batteryHealth', 'condition'].includes(handle);

  const handleVariantSelection = (suggested: boolean) => {
    if (suggested) setWasSuggested(handle);
    else setWasSuggested(null);
  };

  // We're hiding the Gender and Color variants for Golf Categories...
  // Instead of Fixing the data, we hard code it in the storefront 🙈
  if (
    (categoryParentName.toLowerCase().includes('golf clubs') ||
      category.toLowerCase().includes('golf clubs')) &&
    ['gender', 'color'].includes(rVariant.handle)
  )
    return null;

  const variantNameLabel = generateVariantNameLabel(name, handle);

  return (
    <div
      key={`${rVariant.name}-${rVariant.handle}`}
      id={`e2e-product-${handle}`}
    >
      {/* Variant Title */}
      <h3 className="flex items-center gap-1 text-xxs font-bold uppercase text-gray-700 lg:font-normal">
        <div className="flex items-center">
          {variantNameLabel}
          {isColorVariant && currentColor && (
            <>
              :<span className="ml-1 block font-bold">{currentColor}</span>
            </>
          )}
        </div>
        {needsMoreInfo && (
          <button
            className="-mt-0.5 items-center outline-none"
            onClick={handleVisibleModal}
          >
            <Image
              src={infoIconBlack}
              alt={name}
              width={12}
              height={12}
              className="object-contain"
            />
          </button>
        )}
      </h3>

      {/* Variant Options */}
      <div
        className={cn('flex flex-wrap gap-y-2', {
          'mt-1.5 gap-x-1.5': isColorVariant,
          '-pr-2': !isColorVariant,
        })}
      >
        {values.map((value) => {
          const option = streamlineOption(handle, value);

          const {
            suggested,
            offer: matchingOffer,
            shouldHide,
          } = offerTree.get(
            {
              ...(router.query as Record<string, string>),
              ...selection,
              [handle]: value,
            },
            {
              [handle]: value,
              ...(handle !== 'serviceProvider' && {
                serviceProvider: selection.serviceProvider,
              }),
              ...(handle !== 'dexterity' && {
                dexterity: selection.dexterity,
              }),
            },
          );

          if (shouldHide) return null;

          const queryParams = new URLSearchParams({
            ...(router.query as Record<string, string>),
            ...generateSelection(matchingOffer?.rVariants),
          });
          const isSelected = selection[handle] === value;

          if (isColorVariant) {
            return (
              <ColorOption
                key={`${handle}-${value}`}
                id={`e2e-product-${handle}-${value}`}
                queryParams={queryParams}
                setCurrentColor={setCurrentColor}
                offerColor={offerColor}
                isSelected={isSelected}
                option={option}
                suggested={suggested}
                handleVariantSelection={() => handleVariantSelection(suggested)}
                colors={getColors()}
                handle={handle}
              />
            );
          }

          if (handle === 'condition') {
            return (
              <ConditionOption
                key={`${handle}-${value}`}
                id={`e2e-product-${handle}-${value}`}
                queryParams={queryParams}
                isSelected={isSelected}
                option={option}
                matchingOffer={matchingOffer}
                suggested={suggested}
                handleVariantSelection={() => handleVariantSelection(suggested)}
                className={FULL_WIDTH_OPTION_STYLE}
                handle={handle}
              />
            );
          }

          if (handle === 'batteryHealth') {
            return (
              <BatteryHealthOption
                key={`${handle}-${value}`}
                id={`e2e-product-${handle}-${value}`}
                queryParams={queryParams}
                isSelected={isSelected}
                option={option}
                matchingOffer={matchingOffer}
                suggested={suggested}
                handleVariantSelection={() => handleVariantSelection(suggested)}
                offer={offer}
                className={OPTION_STYLE}
                handle={handle}
              />
            );
          }

          return (
            <DefaultOption
              key={`${handle}-${value}`}
              id={`e2e-product-${handle}-${value}`}
              queryParams={queryParams}
              isSelected={isSelected}
              option={option}
              suggested={suggested}
              handleVariantSelection={() => handleVariantSelection(suggested)}
              className={cn({
                [OPTION_STYLE]: handle !== 'serviceProvider',
                [FULL_WIDTH_OPTION_STYLE]: handle === 'serviceProvider',
              })}
              handle={handle}
            />
          );
        })}
      </div>
      {wasSuggested === handle && (
        <div className="mt-2 text-xs text-[#136943]">
          Updated other options based on this selection
        </div>
      )}
      {handle === 'condition' && selection.condition === 'Premium' && (
        <PremiumCondition className="mt-4" />
      )}
      <ConditionPopup
        activeCondition={selection?.condition ?? ''}
        isOpen={isConditionVisible}
        onClose={() => setIsConditionVisible(false)}
        categoryPk={categoryPk}
      />
      <BatteryHealthModal
        isVisible={isBatteryHealthVisible}
        hideModal={() => setIsBatteryHealthVisible(false)}
      />
    </div>
  );
};

export default Variant;
